import { useCallback, useState } from 'react';

import { MediaAudio } from '#components/Media/MediaAudio';
import { MediaContextPlaybackStatus } from '#cutils/context/MediaContext';
import { getDurationFromMediaModule } from '#cutils/media/getDurationFromMediaModule';
import { getIsMediaOffline } from '#cutils/media/getIsMediaOffline';
import { logger } from '#utils/logging/logger';

import styles from '../../../../shared/styles/mediacontainer.module.scss';

import { ArticleModuleMedia } from './ArticleModuleMedia';
import { ArticleModuleMediaCollapsibleMetadataBox } from './ArticleModuleMediaCollapsibleMetadataBox';
import { ARTICLE_MODULE_MEDIA_IMAGE_SIZES } from './ArticleModuleMediaSize';
import type { ArticleModuleAudioFragment } from './__generated__/ArticleModuleAudio.generated';

type Props = {
	audioModule: ArticleModuleAudioFragment;
	publicationDate: string | null | undefined;
};

export function ArticleModuleAudio({ audioModule, publicationDate }: Props) {
	const audioDuration = getDurationFromMediaModule(audioModule);
	const audioUrl = audioModule.audio?.url ?? null;
	const thumbnail = audioModule?.thumbnail;
	const isMediaOffline = getIsMediaOffline(audioModule.meta);

	const [mediaPlaybackStatus, setMediaPlaybackStatus] = useState(MediaContextPlaybackStatus.STOPPED);

	const handlePlaybackStatusChange = useCallback((e: MediaContextPlaybackStatus) => {
		setMediaPlaybackStatus(e);
	}, []);

	if (!audioUrl) {
		logger.error(`module is of type 'audio', but no audio or url was found in '%s'.`, audioModule.rowId);

		return null;
	}

	if (!thumbnail) {
		logger.error(`No thumbnail for audio with module id '%s' found.`, audioModule.rowId);

		return null;
	}

	const collapsibleMetadataBox = (
		<ArticleModuleMediaCollapsibleMetadataBox
			articleModule={audioModule}
			mediaPlaybackStatus={mediaPlaybackStatus}
			moduleType={isMediaOffline ? 'IMAGE' : audioModule.type}
		/>
	);
	const audio = (
		<div className={styles.mediaRoot}>
			<MediaAudio
				copyright={audioModule.copyright}
				duration={audioDuration}
				meta={audioModule.meta}
				publicationDate={publicationDate}
				thumbnailAltText={thumbnail.altText}
				thumbnailCopyright={thumbnail.copyright}
				thumbnailUrl={thumbnail.url}
				title={audioModule.title || undefined}
				url={audioUrl}
				onPlayStatusChange={handlePlaybackStatusChange}
				rowId={audioModule.rowId}
				sizes={ARTICLE_MODULE_MEDIA_IMAGE_SIZES}
			/>
		</div>
	);

	return <ArticleModuleMedia collapsibleMetadataBox={collapsibleMetadataBox} media={audio} />;
}
